import React from "react";

export default function PositionsPanel({
    nestedPositions, // label → asset → symbol → strategy → positions
    openLabel,
    setOpenLabel,
    openAsset,
    setOpenAsset,
    openSymbol,
    setOpenSymbol,
    openStrategy,
    setOpenStrategy,
    openExitModal,
    openExitModalForMultiple,
    clientBrokerMap,

}) {
    // ------------------
    // Helpers
    // ------------------

    const formatCurrency = (val) => {
        return val ? val.toLocaleString("en-IN") : "0";
    };

    // Payload for array of positions
    const mapPayload = (arr) =>
        arr.map((p) => ({
            symbol: p.symbol,
            side: p.side,
            strategy: p.strategy,
            netQty: p.net_qty,
            clientId: p.client_id,
            broker: clientBrokerMap[p.client_id],
        }));

    // Single position in Exit-All format
    const getPayloadForSingle = (p) => mapPayload([p]);

    // Chevron Component
    const ChevronIcon = ({ isOpen }) => (
        <svg
            className={`w-4 h-4 transition-transform duration-300 ${isOpen ? "rotate-180" : ""
                }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
            />
        </svg>
    );

    // Collect available assets (for Exit All top bar)
    const availableAssets = nestedPositions.flatMap((lbl) =>
        Object.entries(lbl.assets).map(([assetName, assetObj]) => {
            const totalPositions = Object.values(assetObj).reduce(
                (sum, sym) =>
                    sum +
                    Object.values(sym).reduce((c, strat) => c + strat.length, 0),
                0
            );
            return {
                label: lbl.label,
                assetName,
                assetObj,
                totalPositions,
            };
        })
    );

    return (
        <div className="flex flex-col h-full bg-[#0f1115] text-gray-200 font-sans text-sm">

            {/* ------------------ */}
            {/* TOP BAR EXIT ALL */}
            {/* ------------------ */}
            <div className="p-4 border-b border-gray-800 bg-[#161922]">
                <div className="flex flex-wrap gap-3">
                    {availableAssets
                        .filter((a) => a.totalPositions > 0)
                        .map(({ label, assetName, assetObj, totalPositions }) => {
                            // Strategy-level nested payload
                            const flatList = Object.values(assetObj)
                                .flatMap((sym) => Object.values(sym))
                                .flat();

                            return (
                                <button
                                    key={`${label}_${assetName}`}
                                    onClick={() => openExitModalForMultiple(mapPayload(flatList))}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold shadow-md bg-rose-600 text-white hover:opacity-90"
                                >
                                    <span>Exit All {assetName} of {label}</span>
                                    <span className="bg-black/20 px-1.5 rounded text-[10px]">
                                        {totalPositions}
                                    </span>
                                </button>
                            );
                        })}
                </div>
            </div>

            {/* ------------------ */}
            {/* MAIN SCROLL AREA */}
            {/* ------------------ */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {nestedPositions.map((labelBlock) => {
                    const isLabelOpen = openLabel[labelBlock.label];

                    // Label counts
                    const totalPositions = Object.values(labelBlock.assets).reduce(
                        (sum, asset) =>
                            sum +
                            Object.values(asset).reduce(
                                (s2, sym) =>
                                    s2 +
                                    Object.values(sym).reduce(
                                        (s3, strat) => s3 + strat.length,
                                        0
                                    ),
                                0
                            ),
                        0
                    );

                    const labelPayload = Object.values(labelBlock.assets)
                        .flatMap((asset) => Object.values(asset))
                        .flatMap((sym) => Object.values(sym))
                        .flat();

                    return (
                        <div
                            key={labelBlock.label}
                            className="border border-gray-700/50 rounded-lg overflow-hidden bg-[#1a1d26]"
                        >
                            {/* ------------------ */}
                            {/* LEVEL 1: LABEL */}
                            {/* ------------------ */}
                            <div
                                className="p-3 bg-[#242837] border-l-4 border-indigo-500 cursor-pointer flex justify-between items-center hover:bg-[#2e3244]"
                                onClick={() =>
                                    setOpenLabel((prev) => ({
                                        ...prev,
                                        [labelBlock.label]: !prev[labelBlock.label],
                                    }))
                                }
                            >
                                <div className="flex items-center gap-3">
                                    <span className="font-bold text-lg text-white tracking-wide">
                                        {labelBlock.label}
                                    </span>
                                    <span className="text-xs text-gray-400 bg-black/20 px-2 py-1 rounded">
                                        {totalPositions} Pos
                                    </span>
                                </div>

                                <div className="flex items-center gap-3">
                                    {!isLabelOpen && totalPositions > 0 && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openExitModalForMultiple(mapPayload(labelPayload));
                                            }}
                                            className="px-3 py-1 bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500 hover:text-white rounded text-xs transition"
                                        >
                                            Exit Tag
                                        </button>
                                    )}
                                    <ChevronIcon isOpen={isLabelOpen} />
                                </div>
                            </div>

                            {/* ------------------ */}
                            {/* LEVEL 2: ASSETS */}
                            {/* ------------------ */}
                            <div
                                className={`transition-all duration-300 ${isLabelOpen ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"
                                    }`}
                            >
                                {Object.entries(labelBlock.assets).map(([assetName, assetObj]) => {
                                    const assetKey = `${labelBlock.label}_${assetName}`;
                                    const isAssetOpen = openAsset[assetKey];

                                    // Flatten all positions under asset (symbol -> strategy -> positions)
                                    const assetPayload = Object.values(assetObj)
                                        .flatMap((strategyObj) => Object.values(strategyObj))
                                        .flat();

                                    return (
                                        <div key={assetKey} className="border-t border-gray-800">
                                            {/* ASSET HEADER */}
                                            <div
                                                className="pl-6 pr-3 py-2.5 bg-[#1f2230] border-l-[3px] border-l-gray-600 cursor-pointer flex justify-between items-center hover:bg-[#262a3b]"
                                                onClick={() =>
                                                    setOpenAsset((prev) => ({
                                                        ...prev,
                                                        [assetKey]: !isAssetOpen,
                                                    }))
                                                }
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-gray-500"></div>
                                                    <span className="text-sm font-medium text-gray-200">{assetName}</span>
                                                    <span className="text-[10px] text-gray-500 border border-gray-700 px-1.5 rounded">
                                                        {Object.keys(assetObj).length} Sym
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    {!isAssetOpen && assetPayload.length > 0 && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openExitModalForMultiple(mapPayload(assetPayload));
                                                            }}
                                                            className="text-[10px] text-red-500 hover:underline px-2"
                                                        >
                                                            Exit All
                                                        </button>
                                                    )}
                                                    <ChevronIcon isOpen={isAssetOpen} />
                                                </div>
                                            </div>

                                            {/* ------------------ */}
                                            {/* LEVEL 3: SYMBOLS */}
                                            {/* ------------------ */}
                                            <div
                                                className={`bg-[#14161f] transition-all duration-300 ${isAssetOpen ? "max-h-[3000px] opacity-100 py-1" : "max-h-0 opacity-0 overflow-hidden"
                                                    }`}
                                            >
                                                <div className="pl-6 pr-2">
                                                    {Object.entries(assetObj).map(([symbol, strategyObj]) => {
                                                        const symbolKey = `${assetKey}_${symbol}`;
                                                        const isSymbolOpen = openSymbol[symbolKey];

                                                        // Flatten all positions under symbol
                                                        const symbolPayload = Object.values(strategyObj).flat();

                                                        return (
                                                            <div key={symbolKey} className="mb-2">
                                                                {/* SYMBOL HEADER */}
                                                                <div
                                                                    className="flex items-center justify-between p-2 rounded hover:bg-[#202432] cursor-pointer group border border-transparent hover:border-gray-700"
                                                                    onClick={() =>
                                                                        setOpenSymbol((prev) => ({
                                                                            ...prev,
                                                                            [symbolKey]: !isSymbolOpen,
                                                                        }))
                                                                    }
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-gray-500 text-xs">⤷</span>
                                                                        <span className="text-sm font-semibold text-gray-300">{symbol}</span>
                                                                        <span className="text-[10px] bg-gray-800 text-gray-400 px-1.5 rounded-sm">
                                                                            {symbolPayload.length}
                                                                        </span>
                                                                    </div>

                                                                    <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100">
                                                                        {!isSymbolOpen && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openExitModalForMultiple(mapPayload(symbolPayload));
                                                                                }}
                                                                                className="text-[10px] text-red-500 hover:underline px-2"
                                                                            >
                                                                                Exit All
                                                                            </button>
                                                                        )}
                                                                        <ChevronIcon isOpen={isSymbolOpen} />
                                                                    </div>
                                                                </div>

                                                                {/* ------------------ */}
                                                                {/* LEVEL 4: STRATEGIES */}
                                                                {/* ------------------ */}
                                                                <div
                                                                    className={`pl-6 pr-1 space-y-2 mt-1 transition-all duration-300 ${isSymbolOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"
                                                                        }`}
                                                                >
                                                                    {Object.entries(strategyObj).map(([strategy, posArr]) => {
                                                                        const stratKey = `${symbolKey}_${strategy}`;
                                                                        const isStratOpen = openStrategy?.[stratKey] || false;
                                                                        const totalQty = posArr.reduce((sum, p) => sum + (p.net_qty || p.qty || 0), 0);
                                                                        const totalPnl = posArr.reduce((sum, p) => sum + (p.pl || 0), 0);
                                                                        const isTotalProfit = totalPnl >= 0;
                                                                        return (
                                                                            <div key={stratKey}>
                                                                                {/* STRATEGY HEADER */}
                                                                                <div
                                                                                    className="flex justify-between items-center bg-[#0d0f14] p-2 rounded border border-gray-800 hover:border-gray-700 cursor-pointer"
                                                                                //   onClick={() =>
                                                                                //     setOpenStrategy((prev) => ({
                                                                                //       ...prev,
                                                                                //       [stratKey]: !isStratOpen,
                                                                                //     }))
                                                                                //   }
                                                                                >
                                                                                    <div className="flex items-center gap-3">
                                                                                        <span className="text-xs text-indigo-300">{strategy}</span>

                                                                                        {/* Strategy total positions */}
                                                                                        <span className="text-[10px] bg-gray-800 text-gray-400 px-1.5 rounded-sm">
                                                                                            {posArr.length}
                                                                                        </span>

                                                                                        {/* TOTAL QTY */}
                                                                                        <span className="text-[10px] bg-black/40 text-gray-300 px-2 py-0.5 rounded">
                                                                                            Qty: {totalQty}
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className="flex items-center gap-2">
                                                                                        {!isStratOpen && posArr.length > 0 && (
                                                                                            <button
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    openExitModalForMultiple(mapPayload(posArr));
                                                                                                }}
                                                                                                className="text-[10px] text-red-500 hover:underline px-2"
                                                                                            >
                                                                                                Exit All
                                                                                            </button>
                                                                                        )}
                                                                                        {/* <ChevronIcon isOpen={isStratOpen} /> */}
                                                                                    </div>
                                                                                </div>



                                                                                <div
                                                                                    className={`mt-2 space-y-2 transition-all duration-300 ${isStratOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"
                                                                                        }`}
                                                                                >
                                                                                    {posArr.map((pos, idx) => {
                                                                                        const isBuy = pos.side === 1;
                                                                                        const isProfit = (pos.pl || 0) >= 0;

                                                                                        return (
                                                                                            <div
                                                                                                key={`${pos.client_id}_${pos.symbol}_${pos.side}_${idx}`}
                                                                                                className="relative bg-[#0b0c10] border border-gray-800 rounded-md p-3 hover:border-gray-600 transition-colors shadow-sm"
                                                                                            >
                                                                                                {/* Grid Layout for Data */}
                                                                                                <div className="grid grid-cols-12 gap-4 items-center">

                                                                                                    {/* Col 1: Identity */}
                                                                                                    <div className="col-span-12 md:col-span-3 flex md:block justify-between items-center border-b md:border-b-0 border-gray-800 pb-2 md:pb-0 mb-2 md:mb-0">
                                                                                                        <div className="flex flex-col">
                                                                                                            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Client</span>
                                                                                                            <span className="font-mono text-white font-medium">{pos.client_id}</span>
                                                                                                        </div>
                                                                                                        <div className="md:mt-2">
                                                                                                            <span className="text-[10px] bg-indigo-900/50 text-indigo-300 px-2 py-0.5 rounded border border-indigo-500/20">
                                                                                                                {pos.strategy}
                                                                                                            </span>
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    {/* Col 2: Trade Details */}
                                                                                                    <div className="col-span-6 md:col-span-3 space-y-1">
                                                                                                        <div className="flex items-center gap-2">
                                                                                                            <span className={`text-[10px] px-1.5 rounded font-bold ${isBuy ? "bg-green-900/40 text-green-400 border border-green-800" : "bg-red-900/40 text-red-400 border border-red-800"}`}>
                                                                                                                {isBuy ? "BUY" : "SELL"}
                                                                                                            </span>
                                                                                                            <span className="text-sm text-gray-300 font-mono">
                                                                                                                x {pos.net_qty || pos.qty} Qty
                                                                                                            </span>
                                                                                                        </div>
                                                                                                        <div className="text-xs text-gray-500">
                                                                                                            Avg: <span className="text-gray-300">{formatCurrency(pos.net_avg)}</span>
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    {/* Col 3: Price Info */}
                                                                                                    <div className="col-span-6 md:col-span-3 text-right md:text-left space-y-1">
                                                                                                        <div className="text-xs text-gray-500">
                                                                                                            LTP: <span className="text-yellow-400 font-mono">{formatCurrency(pos.ltp)}</span>
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    {/* Col 4: Action */}
                                                                                                    <div className="col-span-12 md:col-span-3 flex justify-end mt-2 md:mt-0">
                                                                                                        <button
                                                                                                            onClick={() => openExitModalForMultiple(getPayloadForSingle(pos))}
                                                                                                            className="w-full md:w-auto px-4 py-1.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xs font-semibold rounded shadow transition-all flex items-center justify-center gap-2"
                                                                                                        >
                                                                                                            Exit Position
                                                                                                        </button>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );

                })}
            </div>
        </div>
    );
}
